import { useMemo } from "react";
import { CalendarDays, Gamepad2, Sparkles, Store, Music2 } from "lucide-react";
import { useExplore } from "@/lib/ExploreContext";
import { useLang } from "@/lib/i18n";
import { experienceSpots } from "@/lib/experienceAds";
import AdStrip from "@/components/yts/ads/AdStrip";

/**
 * A space no business has bought, filled with a bookable experience.
 *
 * Every unsold banner sends a visitor to something they can actually book —
 * a Viator tour, a FareHarbor operator, a GetYourGuide search — instead of
 * asking them to look at an empty slot. `slot` is the banner's position on the
 * page, so a run of banners walks down the list and never repeats an
 * experience. When there is no catalog to draw on (a page outside the explore
 * data, or the moment before it loads) the space falls back to one of the
 * guide's own invitations, so it is never blank.
 */

const HOUSE_SPOTS = [
  {
    to: "/business",
    icon: Store,
    en: { overline: "Free for island businesses", title: "Claim your listing" },
    es: { overline: "Gratis para negocios de la isla", title: "Reclama tu negocio" },
  },
  {
    to: "/advertise",
    icon: Sparkles,
    en: { overline: "Reach island visitors", title: "Advertise with the guide" },
    es: { overline: "Llega a quien visita la isla", title: "Anúnciate en la guía" },
  },
  {
    to: "/events",
    icon: CalendarDays,
    en: { overline: "This week", title: "What's on across the island" },
    es: { overline: "Esta semana", title: "Qué hay por toda la isla" },
  },
  {
    to: "/el-ensayo",
    icon: Gamepad2,
    en: { overline: "One night, one game", title: "Play El Ensayo" },
    es: { overline: "Una noche, un juego", title: "Juega El Ensayo" },
  },
];

// A stable number per placement, so a banner with no page position of its own
// keeps the same fill between renders.
const seedOf = (placement) =>
  String(placement).split("").reduce((sum, ch) => sum + ch.charCodeAt(0), 0);

export default function ExperienceAd({ placement = "home-mid", slot, compact = false }) {
  const { lang } = useLang();
  const { viatorListings, allListings } = useExplore();
  const es = lang === "es";
  const position = slot ?? seedOf(placement);

  const spots = useMemo(
    () => experienceSpots({ viator: viatorListings, listings: allListings, lang }),
    [viatorListings, allListings, lang]
  );

  // Use selected unsold house inventory to introduce LOOP's Sunset Salsa
  // series. Any paid Active AdCampaign still takes priority in AdSlot.
  // Dates remain unconfirmed; this is a series discovery card, not ticketing.
  if (placement === "events-strip" || (placement === "home-top" && slot === 3)) {
    return (
      <AdStrip
        to="/sunset-salsa-paseo-caribe"
        icon={Music2}
        ariaLabel="Sunset Salsa at Paseo Caribe by LOOP Experience"
        brand="LOOP Experience"
        overline={es ? "Serie cultural / fechas por confirmar" : "Cultural series / dates to be announced"}
        title="SUNSET SALSA / PASEO CARIBE"
        description={es ? "Baile social y clases de salsa en San Juan, con LOOP Experience. Consulta próximas fechas." : "Salsa classes and social dancing at Paseo Caribe, hosted by LOOP. Check the next dates."}
        meta={es ? "San Juan · Próximamente" : "San Juan · Dates TBA"}
        cta={es ? "Explorar" : "Explore"}
        compact={compact}
      />
    );
  }

  // A high-visibility *unsold* home placement features a distinctive
  // San Juan wellness experience. The AdSlot parent preserves paid campaigns.
  // This is an affiliate editorial pick, not a claim of a paid partnership.
  const thermal = viatorListings.find((item) => item.product_code === "5642477P1" && item.booking_url && (item.gallery_images?.length || item.cover_image));
  if (thermal && placement === "home-top" && slot === 0) {
    return (
      <AdStrip
        to={`/experience/${thermal.id}`}
        ariaLabel={es ? thermal.title_es || "Spa termal en Viejo San Juan" : thermal.name}
        brand="viator"
        overline={es ? "DESTACADO / BIENESTAR EN VIEJO SAN JUAN" : "FEATURED / OLD SAN JUAN WELLNESS"}
        title={es ? "Las Ruinas — Spa termal en Viejo San Juan." : "Las Ruinas — Thermal Spa in Old San Juan."}
        description={es
          ? "Circuito de agua fría y caliente, saunas, vapor y gimnasio."
          : "Hot and cold plunge pools, sauna, steam and a wellness gym."}
        meta={es ? "Viejo San Juan · Circuito termal" : "Old San Juan · Thermal circuit"}
        thumb={thermal.cover_image || thermal.gallery_images?.[0]}
        thumbAlt={thermal.name}
        cta={es ? "Explorar" : "View experience"}
        compact={compact}
      />
    );
  }
  if (spots.length) {
    const spot = spots[position % spots.length];
    return (
      <AdStrip
        href={spot.href}
        ariaLabel={spot.title}
        brand={spot.brand}
        overline={spot.overline}
        title={spot.title}
        description={spot.blurb}
        meta={spot.meta}
        thumb={spot.thumb}
        thumbs={spot.thumbs}
        thumbAlt={spot.title}
        cta={spot.cta}
        compact={compact}
      />
    );
  }

  const invite = HOUSE_SPOTS[position % HOUSE_SPOTS.length];
  const copy = es ? invite.es : invite.en;

  return (
    <AdStrip
      to={invite.to}
      icon={invite.icon}
      ariaLabel={copy.title}
      overline={copy.overline}
      title={copy.title}
      compact={compact}
    />
  );
}